import React, { useEffect } from 'react';
import { Container, Group, Box, Text, ActionIcon, Stack, Button, Flex } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconBrandFacebook, IconBrandInstagram, IconBrandYoutube } from '@tabler/icons-react';
import newLogoWhite from '@/assets/images/new-wcwd-logo-white.svg';

// Footer component with positioning fixes and responsive design
const Footer = () => {
  // Use effect to ensure the footer is positioned properly in the DOM
  useEffect(() => {
    // Function to fix the footer position
    const fixFooterPosition = () => {
      const appContainer = document.querySelector('.app-container') || document.querySelector('#app');
      const footer = document.querySelector('footer');
      const mainContent = document.querySelector('main') || document.querySelector('.main-content');

      if (!footer || !appContainer) return;

      // If the footer exists but is in the wrong position (before main or right after navbar),
      // move it to the end of the container
      if (mainContent && footer.compareDocumentPosition(mainContent) & Node.DOCUMENT_POSITION_FOLLOWING) {
        console.log('⚠️ Footer is before main content - fixing position');
        // Move footer to end of app container
        appContainer.appendChild(footer);
      }

      // Apply styles directly to ensure it works
      footer.style.marginTop = 'auto';
      footer.style.width = '100%';
    };

    // Execute after a slight delay to ensure DOM is ready
    setTimeout(fixFooterPosition, 100);

    // Also run on any navigation changes
    window.addEventListener('popstate', fixFooterPosition);

    return () => {
      window.removeEventListener('popstate', fixFooterPosition);
    };
  }, []);

  // Define breakpoints using useMediaQuery hook
  const isMobile = useMediaQuery('(max-width: 576px)');
  const isTablet = useMediaQuery('(min-width: 577px) and (max-width: 768px)');
  const isDesktop = useMediaQuery('(min-width: 769px)');

  return (
    <Box
      component="footer"
      bg="#09090B"
      py="md"
      style={{
        color: 'white',
        width: '100%',
        marginTop: 'auto',
      }}
    >
      <Container size="lg">
        {isDesktop && (
          <Flex justify="space-between" gap="xl">
            {/* Left section - Logo and social links */}
            <Stack gap="lg">
              <Box>
                <img
                  src={newLogoWhite}
                  alt="Where Can We Dance Logo"
                  style={{ marginTop: '12px', height: '40px', width: 'auto' }}
                />
              </Box>

              <Box>
                <Text mb="md" size="md" fw={700} c="#f2f2f290">
                  Follow Us
                </Text>
                <Group gap="xs">
                  <ActionIcon size="lg" variant="subtle" color="gray">
                    <IconBrandFacebook size={24} />
                  </ActionIcon>
                  <ActionIcon size="lg" variant="subtle" color="gray">
                    <IconBrandInstagram size={24} />
                  </ActionIcon>
                  <ActionIcon size="lg" variant="subtle" color="gray">
                    <IconBrandYoutube size={24} />
                  </ActionIcon>
                </Group>
              </Box>
            </Stack>

            {/* Middle section - Quick Links */}
            <Flex gap="xl">
              {/* Quick Links column */}
              <Box>
                <Text mb="md" size="md" fw={700} c="#f2f2f290">
                  Quick Links
                </Text>
                <Stack gap="xs">
                  <Text component="a" href="#" size="sm" color="gray.3">
                    Features
                  </Text>
                  <Text component="a" href="#" size="sm" color="gray.3">
                    Feedback
                  </Text>
                  <Text component="a" href="#" size="sm" color="gray.3">
                    Help center
                  </Text>
                  <Text component="a" href="#" size="sm" color="gray.3">
                    Contact us
                  </Text>
                </Stack>
              </Box>

              {/* Resources column */}
              <Box>
                <Text mb="md" size="md" fw={700} c="#f2f2f290">
                  Resources
                </Text>
                <Stack gap="xs">
                  <Text component="a" href="#" size="sm" color="gray.3">
                    Artist Submission
                  </Text>
                  <Text component="a" href="#" size="sm" color="gray.3">
                    Community Guidelines
                  </Text>
                  <Text component="a" href="#" size="sm" color="gray.3">
                    Terms & Privacy
                  </Text>
                  <Text component="a" href="#" size="sm" color="gray.3">
                    GDPR
                  </Text>
                </Stack>
              </Box>
            </Flex>

            {/* Right section - Newsletter and Support */}
            <Stack gap="xl" style={{ maxWidth: '300px' }}>
              <Box>
                <Text mb="md" size="md" fw={700} c="#f2f2f290">
                  Newsletter
                </Text>
                <Text size="sm" mb="md">
                  Be the first to know about our future updates!
                </Text>
                <Flex>
                  <Button variant="filled" color="#EA580C" radius="md" fullWidth>
                    Subscribe
                  </Button>
                </Flex>
              </Box>

              <Box>
                <Text mb="md" size="md" fw={700} c="#f2f2f290">
                  Support this project
                </Text>
                <Text size="sm" mb="md">
                  We're consistently working on new releases.
                </Text>
                <Flex>
                  <Button variant="filled" color="#EA580C" radius="md" fullWidth>
                    Buy us a star
                  </Button>
                </Flex>
              </Box>
            </Stack>
          </Flex>
        )}

        {isMobile && (
          <Stack spacing="xl">
            {/* Logo and social media */}
            <Flex justify="space-between" align="center">
              <Box>
                <img src={newLogoWhite} alt="Where Can We Dance Logo" style={{ height: '30px', width: 'auto' }} />
              </Box>

              <Group gap="xs">
                <ActionIcon size="md" variant="subtle" color="gray">
                  <IconBrandFacebook size={20} />
                </ActionIcon>
                <ActionIcon size="md" variant="subtle" color="gray">
                  <IconBrandInstagram size={20} />
                </ActionIcon>
                <ActionIcon size="md" variant="subtle" color="gray">
                  <IconBrandYoutube size={20} />
                </ActionIcon>
              </Group>
            </Flex>

            {/* Quick links and resources in a two-column grid */}
            <Flex justify="space-between" gap="md">
              {/* Quick Links column */}
              <Box style={{ flex: 1 }}>
                <Text mb="sm" size="sm" fw={700} c="#f2f2f290">
                  Quick Links
                </Text>
                <Stack gap="xs" spacing="xs">
                  <Text component="a" href="#" size="xs" color="gray.3">
                    Features
                  </Text>
                  <Text component="a" href="#" size="xs" color="gray.3">
                    Feedback
                  </Text>
                  <Text component="a" href="#" size="xs" color="gray.3">
                    Help center
                  </Text>
                  <Text component="a" href="#" size="xs" color="gray.3">
                    Contact us
                  </Text>
                </Stack>
              </Box>

              {/* Resources column */}
              <Box style={{ flex: 1 }}>
                <Text mb="sm" size="sm" fw={700} c="#f2f2f290">
                  Resources
                </Text>
                <Stack gap="xs" spacing="xs">
                  <Text component="a" href="#" size="xs" color="gray.3">
                    Artist Submission
                  </Text>
                  <Text component="a" href="#" size="xs" color="gray.3">
                    Community Guidelines
                  </Text>
                  <Text component="a" href="#" size="xs" color="gray.3">
                    Terms & Privacy
                  </Text>
                  <Text component="a" href="#" size="xs" color="gray.3">
                    GDPR
                  </Text>
                </Stack>
              </Box>
            </Flex>

            {/* Newsletter and Support */}
            <Stack spacing="lg">
              <Box>
                <Text mb="xs" size="sm" fw={700} c="#f2f2f290">
                  Newsletter
                </Text>
                <Text size="xs" mb="sm">
                  Be the first to know about our future updates!
                </Text>
                <Button variant="filled" color="#EA580C" radius="md" fullWidth size="sm">
                  Subscribe
                </Button>
              </Box>

              <Box>
                <Text mb="xs" size="sm" fw={700} c="#f2f2f290">
                  Support this project
                </Text>
                <Text size="xs" mb="sm">
                  We're consistently working on new releases.
                </Text>
                <Button variant="filled" color="#EA580C" radius="md" fullWidth size="sm">
                  Buy us a star
                </Button>
              </Box>
            </Stack>
          </Stack>
        )}

        {isTablet && (
          <Stack spacing="xl">
            {/* Logo and social media in a row */}
            <Flex justify="space-between" align="center">
              <Box>
                <img src={newLogoWhite} alt="Where Can We Dance Logo" style={{ height: '35px', width: 'auto' }} />
              </Box>

              <Group gap="xs">
                <ActionIcon size="lg" variant="subtle" color="gray">
                  <IconBrandFacebook size={22} />
                </ActionIcon>
                <ActionIcon size="lg" variant="subtle" color="gray">
                  <IconBrandInstagram size={22} />
                </ActionIcon>
                <ActionIcon size="lg" variant="subtle" color="gray">
                  <IconBrandYoutube size={22} />
                </ActionIcon>
              </Group>
            </Flex>

            {/* Three-column layout for tablets */}
            <Flex justify="space-between" gap="md">
              {/* Quick Links column */}
              <Box style={{ flex: 1 }}>
                <Text mb="md" size="sm" fw={700} c="#f2f2f290">
                  Quick Links
                </Text>
                <Stack gap="xs">
                  <Text component="a" href="#" size="sm" color="gray.3">
                    Features
                  </Text>
                  <Text component="a" href="#" size="sm" color="gray.3">
                    Feedback
                  </Text>
                  <Text component="a" href="#" size="sm" color="gray.3">
                    Help center
                  </Text>
                  <Text component="a" href="#" size="sm" color="gray.3">
                    Contact us
                  </Text>
                </Stack>
              </Box>

              {/* Resources column */}
              <Box style={{ flex: 1 }}>
                <Text mb="md" size="sm" fw={700} c="#f2f2f290">
                  Resources
                </Text>
                <Stack gap="xs">
                  <Text component="a" href="#" size="sm" color="gray.3">
                    Artist Submission
                  </Text>
                  <Text component="a" href="#" size="sm" color="gray.3">
                    Community Guidelines
                  </Text>
                  <Text component="a" href="#" size="sm" color="gray.3">
                    Terms & Privacy
                  </Text>
                  <Text component="a" href="#" size="sm" color="gray.3">
                    GDPR
                  </Text>
                </Stack>
              </Box>

              {/* Newsletter and Support */}
              <Box style={{ flex: 1 }}>
                <Stack spacing="md">
                  <Box>
                    <Text mb="xs" size="sm" fw={700} c="#f2f2f290">
                      Newsletter
                    </Text>
                    <Text size="sm" mb="sm">
                      Get our updates!
                    </Text>
                    <Button variant="filled" color="#EA580C" radius="md" fullWidth size="sm">
                      Subscribe
                    </Button>
                  </Box>

                  <Box>
                    <Text mb="xs" size="sm" fw={700} c="#f2f2f290">
                      Support us
                    </Text>
                    <Button variant="filled" color="#EA580C" radius="md" fullWidth size="sm">
                      Buy us a star
                    </Button>
                  </Box>
                </Stack>
              </Box>
            </Flex>
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default Footer;
